import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHero } from '@/components/PagePlaceholder';
import { PosterCard } from '@/components/Poster';
import { Input } from '@/components/ui/Input';
import { Search } from '@/components/icons';
import { catalogApi } from '@/features/catalog/api';

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initial = params.get('q') ?? '';
  const [term, setTerm] = useState(initial);
  const [debounced, setDebounced] = useState(initial);

  // Debounce input → query + URL.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(term);
      setParams(term ? { q: term } : {}, { replace: true });
    }, 300);
    return () => clearTimeout(id);
  }, [term, setParams]);

  const q = debounced.trim();
  const { data, isFetching } = useQuery({
    queryKey: ['search', q],
    queryFn: () => catalogApi.search(q),
    enabled: q.length >= 2,
  });

  return (
    <div>
      <PageHero eyebrow="Search" title="Find something to binge.">
        <div className="relative max-w-xl">
          <Search size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
          <Input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search shows, genres…"
            className="h-12 pl-11"
            aria-label="Search"
          />
        </div>
      </PageHero>

      <div className="container-page pb-16">
        {q.length < 2 ? (
          <p className="text-sm text-ink-faint">Type at least two characters to search.</p>
        ) : isFetching ? (
          <p className="text-sm text-ink-faint">Searching…</p>
        ) : (data?.shows.length ?? 0) === 0 ? (
          <p className="text-sm text-ink-muted">No results for “{q}”.</p>
        ) : (
          <>
            <p className="mb-4 text-sm text-ink-faint">
              {data!.shows.length} result{data!.shows.length === 1 ? '' : 's'} for “{q}”
            </p>
            <div className="flex flex-wrap gap-3">
              {data!.shows.map((s) => (
                <PosterCard key={s.id} show={s} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
